/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Pribyl (pribyl@cadwork.cz)
 *
 */

import RenderObject from "App/geometry/objects/object";
import VertexDataDescriptor from "App/geometry/objects_geometry/vertex_data_descriptor";
import IndexedFacetSet from "App/geometry/objects_geometry/indexed_face_set";
import AbstractGeometry from "App/geometry/objects_geometry/abstract_geometry";
import {mat4} from "gl-matrix";
import Factory from "App/geometry/objects_geometry/factory";

export default
class VolumetricIndexedFaceSet extends IndexedFacetSet {

    public static TypeName: string = "volumetricIndexedFaceSet";

    public layersDesc: VertexDataDescriptor = null;

    constructor(aObject: RenderObject) {
        super(aObject);
    }

    public static create(aObject: RenderObject) {
        return new VolumetricIndexedFaceSet(aObject);
    }

    public clear() {
        super.clear();

        this.layersDesc = null;
    }

    public loadData(aBuffer: Uint8Array) {
        let data: any = {
            positions: null,
            offsets: null,
            normals: null,
            texCoords: null,
            layers: null,
            indices: null
        };

        // this ssi the billboard local geometry
        data.positions = new Float32Array(this.properties.positions);

        // this is offset of the billboard
        data.offsets = new Float32Array(this.properties.positions.length);
        for (let i = 0; i < this.properties.positions.length; i += 3) {
            data.offsets[i + 0] = this.properties.offset[0];
            data.offsets[i + 1] = this.properties.offset[1];
            data.offsets[i + 2] = this.properties.offset[2];
        }

        let repetitionTrafo = this.object.getElement().getRepetitionTransformation();
        if (repetitionTrafo) {
            data.offsets = AbstractGeometry.transformArray(
                data.offsets,
                this.properties.vertexCount,
                new Float32Array(repetitionTrafo)
            );
        }

        if (this.properties.normals !== undefined) {
            data.normals = new Float32Array(this.properties.normals);
        } else {
            data.normals = new Float32Array(this.properties.normalCount * 3);
        }

        if (repetitionTrafo) {
            let rotationMat = mat4.clone(new Float32Array(repetitionTrafo));
            rotationMat[3] = 0.0;
            rotationMat[7] = 0.0;
            rotationMat[11] = 0.0;
            rotationMat[12] = 0.0;
            rotationMat[13] = 0.0;
            rotationMat[14] = 0.0;
            rotationMat[15] = 1.0;
            data.normals = AbstractGeometry.transformArray(
                data.normals,
                this.properties.normalCount,
                rotationMat
            );
        }

        if (this.properties.texCoords !== undefined) {
            data.texCoords = new Float32Array(this.properties.texCoords);
        } else {
            data.texCoords = new Float32Array(this.properties.texCoordCount * 2);
        }

        if (this.properties.layers !== undefined) {
            data.layers = new Float32Array(this.properties.layers);
        } else {
            data.texCoords = new Float32Array(this.properties.texCoordCount * 2);
        }

        data.indices = new Uint32Array(this.properties.indices);

        this.setDescriptors(data);

        data = null;
    }

    protected setDescriptors(aData: any) {
        let positionAttributeSize = this.object.getPositionAttributeSize();
        this.vertexCount = aData.positions.byteLength / (positionAttributeSize * 4);

        this.positionsDesc = new VertexDataDescriptor(
            "positions",
            positionAttributeSize,
            aData.positions.length / positionAttributeSize,
            "FLOAT",
            4,
            0,
            aData.positions
        );

        this.normalsDesc = new VertexDataDescriptor(
            "normals",
            3,
            aData.normals.length / 3,
            "FLOAT",
            4,
            0,
            aData.normals
        );

        this.texCoordsDesc = new VertexDataDescriptor(
            "texCoords",
            2,
            aData.texCoords.length / 2,
            "FLOAT",
            4,
            0,
            aData.texCoords
        );

        this.indicesDesc = new VertexDataDescriptor(
            "indices",
            1,
            aData.indices.length,
            "UINT",
            4,
            0,
            aData.indices
        );

        this.numIndices = aData.indices.length;
        this.numFaces = aData.indices.length / this.object.getPrimitiveSize();

        this.layersDesc = new VertexDataDescriptor(
            "layers",
            1,
            aData.layers.length,
            "FLOAT",
            4,
            0,
            aData.layers
        );
    }

    public releaseGeometryData() {
        this.positionsDesc = null;
        this.normalsDesc = null;
        this.texCoordsDesc = null;
        this.layersDesc = null;

        this.properties.positions = null;
        this.properties.normals = null;
        this.properties.texCoords = null;
        this.properties.layers = null;
        this.properties.indices = null;

        let chunkLodNode: any = this.object.getElement().getSceneAPIElementReference();
        chunkLodNode.releaseData();
        this.positionsDesc = null;
    }
}

Factory.register(VolumetricIndexedFaceSet);